import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { setCanonical, setMeta } from '@/utils/seo';
import { ArticleSchema, BreadcrumbSchema, SpeakableSchema } from '@/components/SchemaMarkup';
import { Calendar, ArrowLeft } from 'lucide-react';
import HeroBackground from '@/components/HeroBackground';
import api from '@/utils/api';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => {
      setPost(r.data);
      setCanonical(`/blog/${slug}`);
      setMeta(`${r.data.title} | CompareWorkersComp.com`, r.data.meta_description || '');
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-cwc-gold border-t-transparent rounded-full" /></div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center text-gray-500">Post not found</div>;

  return (
    <>
      <ArticleSchema title={post.title} description={post.meta_description} slug={post.slug} datePublished={post.created_at} dateModified={post.updated_at} />
      <BreadcrumbSchema items={[{ name: 'Home', url: '/' }, { name: 'Blog', url: '/blog' }, { name: post.title }]} />
      <SpeakableSchema name={post.title} url={`https://compareworkerscomp.com/blog/${slug}`} />

      <section className="relative pt-24 pb-12 overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6 transition-colors"><ArrowLeft className="w-4 h-4" /> Back to Blog</Link>
          <p className="speakable-intro text-white/30 text-xs mb-3">{`Read ${post.title} — a workers compensation insurance guide for California business owners from CompareWorkersComp.com.`}</p>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 hero-text-shadow" data-testid="blog-post-h1">{post.title}</h1>
          <div className="flex items-center gap-2 text-white/40 text-sm">
            <Calendar className="w-4 h-4" />
            {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </div>
      </section>

      <article className="bg-white py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg prose-cwc" data-testid="blog-post-content" dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>

      <section className="bg-cwc-navy py-12">
        <div className="max-w-3xl mx-auto text-center px-4">
          <h2 className="text-xl font-bold text-white mb-3">Need Workers Comp Coverage?</h2>
          <Link to="/#lead-form" className="inline-flex bg-cwc-gold text-cwc-navy font-bold px-6 py-3 rounded-full hover:bg-cwc-gold/90 transition-colors text-sm">Compare Free Quotes</Link>
        </div>
      </section>
    </>
  );
}
